import { handleBackToRingDashboard } from '$src/utils/handleBack';
import Header from '$src/components/Steps/Header';
import Collapse from '$src/components/Collapse';
import ContentBox from '$src/components/ContentBox';
import useStores from '$src/hooks/useStores';
import Draggable from '$src/components/Draggable';
import { redirectToURL } from '$src/utils/redirect';
import {
  homeURL,
  VIDEO_URL,
} from '$src/constants/urls';
import getEnvironment from '$src/utils/enviroment';
import { sendCloudwatchRUMError } from '$src/utils/cloudwatchRUM';

import ReactPlayer from 'react-player';

import { Button } from '@amzn/ring-react-components/csr/components/Button';
import { MonitoringStationIcon, HelpCenterIcon, SmartAlertsIcon, TwofaIcon } from '@ring-icons';

import freeTrialHeader from '$src/assets/img/free_trial_header.gif';
import dashboardDisarmed from '$src/assets/img/dashboard_disarmed.png';
import motionSettings from '$src/assets/img/motion_settings.png';
import selectDevices from '$src/assets/img/select_devices.png';
import alertPreferences from '$src/assets/img/alert_preferences.png';


import './index.less';

function UpsellPage() {
  const {
    sessionStore: { isMobile },
  } = useStores();

  return (
    <div className="upsell__main" data-testid="upsell">
      <div className="upsell">
        <Header hideBack handleClose={() => handleBackToRingDashboard(isMobile)}>
          Virtual Security Guard
        </Header>

        <div className="upsell__header__wrapper">
          <img src={freeTrialHeader} alt="free trial header" className="upsell__header" />
        </div>

        <div className="upsell__title">The Highest Level of Security</div>
        <div className="upsell__subtitle">
          Deter break-ins, trespassing, theft, property damage, and more for your home or business with live professional Guards.
        </div>
        <Draggable rootClass="upsell__">
          <div className="upsell__slider-content">
            <ContentBox icon={<MonitoringStationIcon />}>
              <h1>Security Done for You</h1>
              When you arm your Ring Alarm, Virtual Security Guard monitors for people. When a person is detected, Guards from Rapid Response
              immediately live monitor the situation.
            </ContentBox>
            <ContentBox icon={<HelpCenterIcon />}>
              <h1>Proactive Protection</h1>
              Guards provide real-time deterrence using your camera’s speaker and siren, and will immediately contact emergency dispatch for police,
              fire and medical services in the event of an emergency or call you in the event of suspicious activity.
            </ContentBox>
            <ContentBox icon={<SmartAlertsIcon />}>
              <h1>Notifications that Matter</h1>
              Guards will send text messages and/or call you about activity observed on your property. You control what Guards contact you for and how
              they contact you. You can also customize an emergency contact list so others are contacted.
            </ContentBox>
            <ContentBox icon={<TwofaIcon />}>
              <h1>Designed for Privacy</h1>
              You determine which cameras are monitored by Guards and when they are monitored. Guards will only monitor your selected cameras in Home
              or Away mode. You can also set Privacy Zones to prevent Guards from seeing a designated area. When you disarm your system, Guards will
              immediately lose access to your cameras. You are always in control.
            </ContentBox>
          </div>
        </Draggable>

        <div className="upsell__title upsell__title--secondary">How Virtual Security Guard Works</div>
        <div className="upsell__video-wrapper">
          <ReactPlayer
            className="react-player"
            url={VIDEO_URL}
            controls={true}
            volume={0.5}
            width="100%"
            onError={(e) => {
              console.error("Video player error:", e);

              // Send detailed error information to CloudWatch RUM
              sendCloudwatchRUMError(
                'Video player failed to load in UpsellPage',
                new Error('ReactPlayer loading error'),
                true // Mark as unhandled since this is likely the source of the unhandled rejection
              );
            }}
          />
        </div>

        <Draggable rootClass="upsell__">
          <div className="upsell__slider-content" data-testid="upsell_cards_slider_container">
            <ContentBox image={dashboardDisarmed}>
              <h1> You’re in Control </h1>
              Guards only monitor your selected cameras when your system is armed to Home or Away and a person is detected. Guards do not have access
              to your Event History, cannot download videos, and cannot access your cameras when your system is disarmed.
            </ContentBox>
            <ContentBox image={motionSettings}>
              <h1>Customize Your Coverage</h1>
              You can adjust your Motion Zones to cover areas within your camera’s view that you want Guards to monitor. You can also use Privacy
              Zones to exclude areas on your property you don’t want Guards to see.
            </ContentBox>
            <ContentBox image={selectDevices}>
              <h1>Cameras You Choose</h1>
              You select the cameras you want monitored, and you can change these settings at any time. If you decide to unenroll a camera from
              monitoring, events from that camera will immediately stop being sent to Guards for review.
            </ContentBox>
            <ContentBox image={alertPreferences}>
              <h1>Personalize Your Alerts</h1>
              Depending on your alert preferences, Guards will send text notifications to you and your emergency contacts about activity observed at
              your property. If Guards request police, fire, or medical dispatch, they will call you and your emergency contacts.
            </ContentBox>
          </div>
        </Draggable>

        <div className="upsell__title upsell__title--secondary">Frequently Asked Questions</div>

        <div className="upsell__faq" data-testid="upsell__faq">

          <Collapse title="Who will be monitoring my property?">
            When a person is detected, highly trained Guards from Rapid Response, Ring's trusted Alarm Operating Center, will review the event in
            real-time. Guards are based in controlled facilities in the US and undergo extensive response training to protect your home or business.
          </Collapse>

          <Collapse title="How will I know Guards are only monitoring where and when I want?">
            You’re in complete control over which cameras are monitored by Guards and when, and you can update your enrolled cameras at any time.
            Guards only monitor when your system is in Home or Away mode and cannot access your camera's Live View unless motion is detected. For
            added control over your monitoring, you can use Motion Zones to control which areas are monitored by Guards, and Privacy Zones to control
            what areas Guards can’t monitor. You can also Snooze your events while your system is in Home or Away mode, which temporarily stops events
            from being sent to Guards for review, for a duration you choose.
          </Collapse>

          <Collapse title="Why should I try Virtual Security Guard?">
            Virtual Security Guard puts the monitoring of your property in the hands of highly-trained professionals, available 24/7 so your property
            is always protected when you arm your Ring system. Guards can provide proactive deterrence while you’re away or sleeping, send
            notifications about activity they observe, and quickly request dispatch of emergency services without an alarm sensor being triggered.
          </Collapse>

          <Collapse title="What do you mean by 'Faster Emergency Response'?">
            Many jurisdictions prioritize emergency response for visually verified events. This means that when Guards visually verify an emergency at
            your location and request police dispatch, they confirm the event prioritization with the dispatcher, and your event may be categorized by
            the dispatcher as a higher priority request than dispatch requests that are not visually verified.
          </Collapse>

        </div>

        <p className="upsell__subtext">
          For additional questions, reach out to our Customer Service team at <a href="tel:1-866-894-8282">1-866-894-8282</a>.          .
        </p>

        <div className="upsell__footer">
          <Button className="stark-button-bordered" type="primary" variant="Link" onClick={() => { redirectToURL(homeURL[getEnvironment()]) }} data-testid="footer__primary-action" >
            Learn More
          </Button>
        </div>
      </div>
    </div>
  );
}

export default UpsellPage;
